import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';

import { translateAllDaySlots } from '@/api/allDaySlots';
import type { RefreshTokenRequest, TokensResponse } from '@/api/api.dto.ts';
import { attachCsrf, clearCsrfTokens } from '@/api/csrf.ts';
import {
  clearAccessTokens,
  clearTokens,
  getAccessToken,
  getRefreshToken,
  getServiceId,
  setTokens,
} from '@/api/tokens.helper.ts';
import { toApiException } from '@/common/error.ts';
import { refreshStoredToken } from '@/utils/biometric';
import { toast } from '@/utils/toast';

const LONG_PULL_TIMEOUT = 10 * 60 * 1000; // 10 minutes
const NORMAL_REQUEST_TIMEOUT = 15000; // 15-second timeout

/**
 * Per-call transfer controls, for the few requests big enough to need them:
 * an abort signal (react-query hands one to every query function) and the
 * download progress of a file that takes long enough to be worth a bar.
 */
export type TransferOptions = Pick<AxiosRequestConfig, 'signal' | 'onDownloadProgress'>;

export enum API {
  AUTH = 'auth',
  PEOPLE = 'people',
  ACTIVITY = 'activity',
  CHAT = 'chat',
  VESSEL = 'vessel',
  SERVICE_PROVIDERS = 'service-providers',
  EXPENSES = 'expenses',
  DOCUMENTS = 'documents',
  NOTIFICATIONS = 'notifications',
}

const APIS: Record<API, AxiosInstance> = {
  [API.AUTH]: axios.create({
    baseURL: `${import.meta.env.VITE_APP_AUTH_URL}`,
    headers: {
      'Content-Type': 'application/json',
    },
  }),

  [API.PEOPLE]: axios.create({
    baseURL: `${import.meta.env.VITE_APP_PEOPLE_URL}`,
    headers: {
      'Content-Type': 'application/json',
    },
    paramsSerializer: {
      indexes: null,
    },
  }),
  [API.ACTIVITY]: axios.create({
    baseURL: `${import.meta.env.VITE_APP_ACTIVITY_URL}`,
    headers: {
      'Content-Type': 'application/json',
    },
    paramsSerializer: {
      indexes: null,
    },
  }),
  [API.VESSEL]: axios.create({
    baseURL: `${import.meta.env.VITE_APP_VESSEL_URL}`,
    headers: {
      'Content-Type': 'application/json',
    },
    paramsSerializer: {
      indexes: null, // ids=1&ids=2 instead of ids[0]=1&ids[1]=2
    },
  }),
  [API.CHAT]: axios.create({
    baseURL: `${import.meta.env.VITE_APP_CHAT_URL}`,
    headers: {
      'Content-Type': 'application/json',
    },
  }),
  [API.SERVICE_PROVIDERS]: axios.create({
    baseURL: `${import.meta.env.VITE_APP_SERVICE_PROVIDERS_URL}`,
    headers: {
      'Content-Type': 'application/json',
    },
  }),
  [API.EXPENSES]: axios.create({
    baseURL: `${import.meta.env.VITE_APP_EXPENSES_URL}`,
  }),
  [API.DOCUMENTS]: axios.create({
    baseURL: `${import.meta.env.VITE_APP_DOCUMENTS_URL || import.meta.env.VITE_APP_SERVICE_PROVIDERS_URL}`,
    headers: {
      'Content-Type': 'application/json',
    },
  }),
  [API.NOTIFICATIONS]: axios.create({
    baseURL: `${import.meta.env.VITE_NOTIFICATION_URL}`,
    headers: {
      'Content-Type': 'application/json',
    },
  }),
};

translateAllDaySlots(APIS[API.ACTIVITY], APIS[API.VESSEL]);

// Add X-Service-Id header interceptor to APIs that require it
const apisRequiringServiceId = [
  API.AUTH,
  API.ACTIVITY,
  API.VESSEL,
  API.SERVICE_PROVIDERS,
  API.CHAT,
  API.EXPENSES,
  API.DOCUMENTS,
  API.PEOPLE,
  API.NOTIFICATIONS,
];

const SERVICE_ID_EXCLUDED = {
  methods: ['POST', 'PUT', 'PATCH', 'DELETE'],
  urls: ['/email', '/me', '/subscriptions', '/skills', '/cruising-experiences', '/invites'],
  // `/service-provider/places`: the Google proxy needs only a signed-in user, and onboarding asks
  // for addresses before there is a vessel to send.
  urlPrefixes: [
    'notifications',
    '/profiles',
    'invites',
    '/rooms',
    '/oauth',
    '/service-provider/places',
  ],
};

const needsServiceId = (config: AxiosRequestConfig): boolean => {
  const method = config.method?.toUpperCase() ?? '';
  const url = config.url ?? '';
  return (
    !SERVICE_ID_EXCLUDED.methods.includes(method) &&
    !SERVICE_ID_EXCLUDED.urls.includes(url) &&
    !SERVICE_ID_EXCLUDED.urlPrefixes.some((prefix) => url.startsWith(prefix))
  );
};

apisRequiringServiceId.forEach((apiKey) => {
  APIS[apiKey].interceptors.request.use((config) => {
    if (config.headers['X-Service-Id']) {
      return config;
    }

    const serviceId = getServiceId();
    if (serviceId) {
      config.headers['X-Service-Id'] = serviceId;
    } else if (needsServiceId(config)) {
      // Silently cancel the request if service ID is not set
      if (import.meta.env.DEV) {
        console.warn(`Service ID is missing. Canceling request to ${config.url}`);
      }
      const controller = new AbortController();
      controller.abort();
      config.signal = controller.signal;
    }
    return config;
  });
});

// After the interceptors above: requests run them last-added first, responses first-added
// first, so a replayed write is not translated twice on its way back.
Object.values(APIS).forEach(attachCsrf);

const setTokensToAPIs = (accessToken?: string) => {
  Object.values(APIS).forEach((api) => {
    if (!api) return;
    if (accessToken) {
      api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    } else {
      delete api.defaults.headers.common.Authorization;
    }
  });
};

const cachedAccessToken = getAccessToken();
setTokensToAPIs(cachedAccessToken || undefined);

let refreshTokenPromise: Promise<boolean> | null = null;
let tokenRefreshVersion = 0;

const refreshToken = async (token: string): Promise<boolean> => {
  try {
    const response = await APIS[API.AUTH].post<
      TokensResponse,
      AxiosResponse<TokensResponse>,
      RefreshTokenRequest
    >(`refresh`, {
      token,
    });
    if (response.status === 200) {
      setTokens(response.data.access_token, response.data.refresh_token);
      setTokensToAPIs(response.data.access_token);
      tokenRefreshVersion += 1;
      // Keep the biometric-stored token in sync after rotation (no-op unless enabled).
      // AWAITED so it can't be lost to app suspension/kill — a fire-and-forget write could leave
      // the keychain with a consumed token => {"domain":"JWT","type":"NOT_FOUND"} on next unlock.
      await refreshStoredToken(response.data.refresh_token);
      return true;
    }
  } catch {
    clearTokens();
    setTokensToAPIs(undefined);
    return false;
  }

  clearTokens();
  setTokensToAPIs(undefined);
  return false;
};

const refreshTokenOnce = (token: string): Promise<boolean> => {
  if (!refreshTokenPromise) {
    refreshTokenPromise = refreshToken(token).finally(() => {
      refreshTokenPromise = null;
    });
  }

  return refreshTokenPromise;
};

const waitForActiveTokenRefresh = async (): Promise<void> => {
  if (!refreshTokenPromise) return;

  const isRefreshed = await refreshTokenPromise;
  if (!isRefreshed) {
    window.location.assign('/auth');
    throw new Error('Token refresh failed');
  }
};

export const refreshAccessTokenNow = async (): Promise<string> => {
  const isRefreshed = await refreshTokenOnce(getRefreshToken());
  return isRefreshed ? getAccessToken() : '';
};

// Helper to check if error is a network error (offline/connection issue)
export const isNetworkError = (error: any): boolean => {
  return (
    error?.code === 'ERR_NETWORK' ||
    error?.message?.includes('Network Error') ||
    error?.message?.includes('Failed to fetch')
  );
};

// Build full URL from baseURL, path, and params (consistent key for cache)
const buildApiUrl = (baseURL: string, url: string, params?: Record<string, any>): string => {
  let fullUrl = `${baseURL}${url}`;
  if (params && Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      fullUrl += url.includes('?') ? `&${queryString}` : `?${queryString}`;
    }
  }
  return fullUrl;
};

// Manually cache a successful API response using the Cache API directly.
// This bypasses CapacitorHttp's fetch patching since Cache API is a separate browser API.
const cacheApiResponse = async (fullUrl: string, data: unknown, status: number): Promise<void> => {
  try {
    if (!('caches' in self)) return;
    // only cache parsed JSON; an HTML error page arrives as a string
    if (data === null || typeof data !== 'object') return;
    const cache = await caches.open('capacitor-api-cache');
    const response = new Response(JSON.stringify(data), {
      status,
      headers: { 'Content-Type': 'application/json' },
    });
    await cache.put(fullUrl, response);
  } catch {
    // Caching is best-effort
  }
};

// Read from Cache API directly (bypasses CapacitorHttp's fetch patching)
const tryFetchFromCache = async <T>(
  baseURL: string,
  url: string,
  params?: Record<string, any>,
): Promise<AxiosResponse<T> | null> => {
  try {
    if (!('caches' in self)) return null;
    const fullUrl = buildApiUrl(baseURL, url, params);
    console.log('Trying to read from cache:', fullUrl);

    const cache = await caches.open('capacitor-api-cache');
    const cachedResponse = await cache.match(fullUrl);

    if (cachedResponse) {
      const data = await cachedResponse.json();
      return {
        data,
        status: cachedResponse.status,
        statusText: cachedResponse.statusText || 'OK',
        headers: Object.fromEntries(cachedResponse.headers.entries()),
        config: {} as any,
      } as AxiosResponse<T>;
    }
    console.log('No cached data for:', fullUrl);
  } catch (e) {
    console.log('Cache read failed:', e);
  }
  return null;
};

const request = async <T, D>(
  apiKey: API,
  url: string,
  method: string,
  data?: D,
  isLongPull = false,
  responseType?: 'json' | 'blob',
  params?: Record<string, any>,
  serviceId?: string,
  transfer?: TransferOptions,
): Promise<AxiosResponse<T>> => {
  const requestDetails: AxiosRequestConfig<D> = {
    method: method,
    url: url,
    timeout: isLongPull ? LONG_PULL_TIMEOUT : NORMAL_REQUEST_TIMEOUT,
    signal: transfer?.signal,
    onDownloadProgress: transfer?.onDownloadProgress,
  };

  if (data) {
    requestDetails.data = data;
  }

  if (responseType) {
    requestDetails.responseType = responseType;
  }

  if (params) {
    requestDetails.params = params;
  }

  if (data instanceof FormData) {
    if (!requestDetails.headers) {
      requestDetails.headers = {};
    }
    requestDetails.headers['Content-Type'] = 'multipart/form-data';
  }

  if (serviceId) {
    requestDetails.headers = { ...requestDetails.headers, 'X-Service-Id': serviceId };
  }

  const api = APIS[apiKey];
  let isRefreshUsed = false;
  while (true) {
    let tokenRefreshVersionBeforeRequest = tokenRefreshVersion;
    try {
      await waitForActiveTokenRefresh();
      tokenRefreshVersionBeforeRequest = tokenRefreshVersion;
      const serviceIdBeforeRequest = getServiceId();
      const response = await api.request<T>(requestDetails);
      // A read that took the active service's id and comes back after an account switch
      // describes the previous service: leave it pending, as with the interceptor's cancel,
      // so it cannot land in a store the switch has just cleared. Callers with a signal are
      // react-query's, which the switch invalidates, and writes have already happened.
      if (
        method === 'GET' &&
        !serviceId &&
        !transfer?.signal &&
        serviceIdBeforeRequest !== getServiceId()
      ) {
        return new Promise<AxiosResponse<T>>(() => {});
      }
      // Cache successful GET responses for offline use
      if (method === 'GET' && responseType !== 'blob') {
        const fullUrl = buildApiUrl(api.defaults.baseURL || '', url, params);
        cacheApiResponse(fullUrl, response.data, response.status);
      }
      return response;
    } catch (error) {
      // A caller that passed its own signal asked for the cancellation and has
      // to be told about it, so react-query can settle the query. Every other
      // cancel comes from an interceptor acting on our behalf (a missing
      // service ID) and no caller can do anything with it: leave it pending.
      if (axios.isCancel(error)) {
        if (transfer?.signal?.aborted) throw error;
        return new Promise<AxiosResponse<T>>(() => {});
      }
      if (axios.isAxiosError(error)) {
        // For GET requests with network errors, try to fetch from service worker cache
        if (method === 'GET' && responseType !== 'blob' && isNetworkError(error)) {
          const cachedResponse = await tryFetchFromCache<T>(
            api.defaults.baseURL || '',
            url,
            params,
          );
          if (cachedResponse) {
            console.log('Offline: serving from service worker cache', url);
            return cachedResponse;
          }
          // No cache available
          toast.error('Couldn’t load everything. Please check your connection.', {
            id: 'offline-error',
          });
        } else if (isNetworkError(error)) {
          toast.error('Couldn’t load everything. Please check your connection.', {
            id: 'offline-error',
          });
        }

        if (error.response) {
          const exception = await toApiException(error.response);
          // Never refresh-on-refresh: a 401 from /refresh means the refresh token is dead.
          // Retrying would re-fail and hard-reload to /auth, which (combined with the
          // biometric auto-prompt) loops Face ID forever after a deleted/expired session.
          // Let it throw so the caller (e.g. loginWithBiometric) can disable biometric.
          const isRefreshCall = apiKey === API.AUTH && (url === '/refresh' || url === 'refresh');
          const shouldTryRefresh =
            !isRefreshCall && (exception.domain === 'JWT' || exception.domain === 'AUTH_HEADER');

          if (shouldTryRefresh) {
            if (tokenRefreshVersionBeforeRequest !== tokenRefreshVersion) {
              isRefreshUsed = true;
              continue;
            }
            if (isRefreshUsed) {
              throw exception;
            }
            clearAccessTokens();
            setTokensToAPIs(undefined);
            const isRefreshed = await refreshTokenOnce(getRefreshToken());
            if (!isRefreshed) {
              window.location.assign('/auth');
              throw exception;
            }
            isRefreshUsed = true;
            continue;
          } else {
            throw exception;
          }
        }
        throw error;
      } else {
        throw error;
      }
    }
  }
};

export const login = (accessToken: string, refreshToken: string) => {
  clearCsrfTokens();
  setTokens(accessToken, refreshToken);
  setTokensToAPIs(accessToken);
};

// Standalone API functions for use outside React components (e.g., Zustand stores)
export const getRequest = async <T>(
  apiKey: API,
  url: string,
  isLongPull = false,
  params?: Record<string, any>,
  serviceId?: string,
  transfer?: TransferOptions,
) =>
  request<T, undefined>(
    apiKey,
    url,
    'GET',
    undefined,
    isLongPull,
    undefined,
    params,
    serviceId,
    transfer,
  );

export const getRequestWithHeaders = async <T>(
  apiKey: API,
  url: string,
  headers: Record<string, string>,
  params?: Record<string, any>,
) => {
  const api = APIS[apiKey];
  return await api.request<T>({
    method: 'GET',
    url,
    headers,
    params,
    timeout: NORMAL_REQUEST_TIMEOUT,
  });
};

export const getRequestBlobWithParams = async (
  apiKey: API,
  url: string,
  params?: Record<string, any>,
): Promise<Blob> => {
  const response = await request<Blob, undefined>(
    apiKey,
    url,
    'GET',
    undefined,
    false,
    'blob',
    params,
  );
  return response.data;
};

export const postRequest = async <T, D>(
  apiKey: API,
  url: string,
  data: D,
  isLongPull = false,
  serviceId?: string,
  transfer?: TransferOptions,
) =>
  request<T, D>(apiKey, url, 'POST', data, isLongPull, undefined, undefined, serviceId, transfer);

export const putRequest = async <T, D>(
  apiKey: API,
  url: string,
  data: D,
  isLongPull = false,
  serviceId?: string,
) => request<T, D>(apiKey, url, 'PUT', data, isLongPull, undefined, undefined, serviceId);

export const patchRequest = async <T, D>(
  apiKey: API,
  url: string,
  data: D,
  isLongPull = false,
  serviceId?: string,
) => request<T, D>(apiKey, url, 'PATCH', data, isLongPull, undefined, undefined, serviceId);

export const deleteRequest = async <T, D = undefined>(
  apiKey: API,
  url: string,
  data?: D,
  isLongPull = false,
  params?: Record<string, any>,
  serviceId?: string,
) =>
  request<T, D | undefined>(apiKey, url, 'DELETE', data, isLongPull, undefined, params, serviceId);

export const postRequestBlob = async <D>(apiKey: API, url: string, data: D): Promise<Blob> => {
  const response = await request<Blob, D>(apiKey, url, 'POST', data, false, 'blob');
  return response.data;
};

export const patchRequestBlob = async <D>(apiKey: API, url: string, data: D): Promise<Blob> => {
  const response = await request<Blob, D>(apiKey, url, 'PATCH', data, false, 'blob');
  return response.data;
};

export const getRequestBlob = async (
  apiKey: API,
  url: string,
  params?: Record<string, any>,
  isLongPull = false,
  transfer?: TransferOptions,
  serviceId?: string,
): Promise<Blob> => {
  const response = await request<Blob, undefined>(
    apiKey,
    url,
    'GET',
    undefined,
    isLongPull,
    'blob',
    params,
    serviceId,
    transfer,
  );
  return response.data;
};

// React hook for use in components
export const useApi = () => {
  return {
    getRequest,
    getRequestWithHeaders,
    postRequest,
    putRequest,
    deleteRequest,
    login,
  };
};
